import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { reportFilterChanges } from '../telemetry';

type Kind = 'text' | 'list' | 'page';

interface Field {
  kind: Kind;
  value: string | string[] | number | undefined;
  set: (next: any) => void;
}

export type UrlFilterSpec = Record<string, Field>;

const LIST_SEP = '|';

const toParam = (field: Field): string => {
  if (field.kind === 'list') {
    const list = Array.isArray(field.value) ? field.value : [];
    return list.join(LIST_SEP);
  }
  if (field.kind === 'page') {
    const page = Number(field.value) || 1;
    return page > 1 ? String(page) : '';
  }
  return field.value == null ? '' : String(field.value);
};

const fromParam = (kind: Kind, raw: string | null) => {
  if (kind === 'list') return raw ? raw.split(LIST_SEP).filter(Boolean) : [];
  if (kind === 'page') {
    const page = parseInt(raw ?? '', 10);
    return page > 0 ? page : 1;
  }
  return raw ?? '';
};

// Keeps list filters and the page number in the query string, both ways: state changes are
// written with history.replace (no history entry per keystroke), and a URL change (back,
// forward, a pasted link) is read back into state. Keys the spec does not mention (?order=)
// are left alone. Empty values are removed rather than left stale.
export default function useUrlFilters(spec: UrlFilterSpec) {
  const history = useHistory();
  const { search } = useLocation();

  const keys = Object.keys(spec);
  const serialized = keys.map((key) => toParam(spec[key])).join(' ');

  // On mount the URL wins (a pasted link): only the read pass runs. Writing on mount too
  // would race it, each pass undoing the other.
  const mounted = useRef(false);

  // state -> URL
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    const params = new URLSearchParams(search);
    const previous: Record<string, string> = {};
    keys.forEach((key) => {
      previous[key] = params.get(key) ?? '';
      const value = toParam(spec[key]);
      if (value) params.set(key, value);
      else params.delete(key);
    });
    const next = params.toString();
    const current = new URLSearchParams(search).toString();
    if (next !== current) {
      reportFilterChanges(
        previous,
        keys.map((key) => ({
          field: key,
          kind: spec[key].kind,
          value: toParam(spec[key]),
        })),
      );
      history.replace({ search: next ? `?${next}` : '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized]);

  // URL -> state. Reads the live location, not this render's `search`: when a filter change
  // also resets the page (a state change in the same commit), the write pass above has just
  // replaced the URL, and reading the stale one back would put the old page number into
  // state again, and the two passes would chase each other forever.
  useEffect(() => {
    const params = new URLSearchParams(history.location.search);
    keys.forEach((key) => {
      const field = spec[key];
      const raw = params.get(key);
      if ((raw ?? '') !== toParam(field)) field.set(fromParam(field.kind, raw));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
}
